import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Building2, GraduationCap, Home, Repeat, X } from "lucide-react";
import { espaceApi, messageErreur, CLE_JETON } from "../espace/api";

/* Une seule application, plusieurs casquettes. Noémie est à la fois la direction de
   l'école, une prof et une maman : elle passe de l'une à l'autre d'un tap, sans se
   reconnecter. Le dernier espace choisi est retenu pour la prochaine ouverture. */
export const CLE_ROLE = "app_role";

export const roleRetenu = () => {
  try { return localStorage.getItem(CLE_ROLE) || ""; } catch { return ""; }
};
const retenir = (role) => {
  try { localStorage.setItem(CLE_ROLE, role); } catch { /* navigation privée */ }
};

/** Les casquettes de la personne connectée à l'app ({ ecole, prof, famille }), ou null. */
export function useRoles() {
  const [roles, setRoles] = useState(null);
  useEffect(() => {
    let jeton = "";
    try { jeton = localStorage.getItem(CLE_JETON) || ""; } catch { /* rien */ }
    if (!jeton) return undefined;
    let actif = true;
    espaceApi.get("/moi").then(({ data }) => {
      if (actif) setRoles({ ...(data.roles || {}), famille: !!data.rattachee });
    }).catch(() => {});
    return () => { actif = false; };
  }, []);
  return roles;
}

/** Ouvre l'espace demandé : « ecole », « prof » ou « famille ». */
export async function ouvrirEspace(role, navigate) {
  retenir(role);
  if (role === "famille") { navigate("/espace"); return; }
  const { data } = await espaceApi.post(`/role/${role}`);
  if (role === "ecole") {
    localStorage.setItem("admin_token", data.jeton);
    navigate("/direction");
  } else {
    navigate(`/prof?jeton=${encodeURIComponent(data.jeton)}`);
  }
}

const CHOIX = [
  { id: "ecole", label: "Mode école", aide: "La console au format téléphone", Icone: Building2, couleur: "#0F1B2D" },
  { id: "prof", label: "Mon espace prof", aide: "Mes cours, mes listes, mes heures", Icone: GraduationCap, couleur: "#9333EA" },
  { id: "famille", label: "Mon espace famille", aide: "Les cours et factures de mes enfants", Icone: Home, couleur: "#00C6CE" },
];

/** Le bouton « changer d'espace » de l'en-tête, avec sa feuille de choix. */
export const BoutonChoixEspace = ({ actuel, roles, clair = false }) => {
  const navigate = useNavigate();
  const [ouvert, setOuvert] = useState(false);
  const [erreur, setErreur] = useState("");
  const possibles = CHOIX.filter((c) => c.id !== actuel && roles?.[c.id]);
  if (!possibles.length) return null;

  const choisir = async (id) => {
    setErreur("");
    try {
      await ouvrirEspace(id, navigate);
      setOuvert(false);
    } catch (e) {
      setErreur(messageErreur(e));
    }
  };

  return (
    <>
      <button onClick={() => setOuvert(true)} aria-label="Changer d'espace"
        className="w-10 h-10 grid place-items-center rounded-full shrink-0"
        style={{ background: clair ? "rgba(255,255,255,0.14)" : "var(--primary-tint)", color: clair ? "#fff" : "var(--text)" }}
        data-testid="choix-espace">
        <Repeat size={17} />
      </button>
      {/* Dans un portail, au-dessus de tout : rendue dans l'en-tête (flou d'arrière-plan),
          la feuille restait enfermée dans la bande de l'en-tête, et sous la barre d'onglets
          sur iPhone (01.11.2026, même cause que les feuilles de l'onglet Factures). */}
      {ouvert && createPortal(
        <div className="fixed inset-0 z-[80] flex items-end" style={{ background: "rgba(15,27,45,0.45)" }}
          onClick={() => setOuvert(false)}>
          <div className="w-full max-w-lg mx-auto bg-white rounded-t-[26px] p-5 space-y-3"
            style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))", color: "var(--text)" }}
            onClick={(e) => e.stopPropagation()} data-testid="choix-espace-feuille">
            <div className="flex items-center justify-between">
              <div className="text-[17px] font-semibold">Changer d'espace</div>
              <button onClick={() => setOuvert(false)} aria-label="Fermer"
                className="w-9 h-9 grid place-items-center rounded-full" style={{ background: "#F1F5F9" }}>
                <X size={16} />
              </button>
            </div>
            {possibles.map(({ id, label, aide, Icone, couleur }) => (
              <button key={id} onClick={() => choisir(id)} data-testid={`choix-espace-${id}`}
                className="w-full flex items-center gap-3 rounded-2xl px-4 py-3.5 text-left active:bg-[#F1F5F9]"
                style={{ border: "1px solid var(--border)" }}>
                <span className="w-10 h-10 rounded-xl grid place-items-center text-white" style={{ background: couleur }}>
                  <Icone size={19} />
                </span>
                <span>
                  <span className="block text-[15px] font-semibold">{label}</span>
                  <span className="block text-[12.5px] text-[var(--muted)]">{aide}</span>
                </span>
              </button>
            ))}
            {erreur && <p className="text-sm" style={{ color: "#B91C1C" }}>{erreur}</p>}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};
